import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Checkbox,
  CircularProgress,
  IconButton,
  LabelDisplayedRowsArgs,
  Table as MuiTable,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { ApiResponse } from '../../types/ApiResponse';
import { LoadState } from '../../types/LoadState';
import { ColumnDefinition } from './ColumnDefinition';
import { Entity } from './Entity';
import TableBody from './TableBody';

interface TableWrapperProps<T extends Entity> {
  data?: T[];
  rowsPerPage: number;
  columnHeaders: any[];
  columnDefinitions: ColumnDefinition<T>[];
  deleteItem?: (item: T) => void;
  deleteMultiIds?: (ids: number[]) => Promise<void>;
  deleteMulti?: (items: T[]) => Promise<ApiResponse>;
  sortColumn?: string;
  sortOrder?: 'asc' | 'desc';
  sortType?: 'Date';
  pageState?: [number, React.Dispatch<React.SetStateAction<number>>];
}

const Table = <T extends Entity>(
  props: React.PropsWithChildren<TableWrapperProps<T>>
) => {
  const [page, setPage] = props.pageState || useState(0);
  const [visibleData, setVisibleData] = useState<T[]>([]);
  const [selected, setSelected] = useState<T[]>([]);
  const [allChecked, setAllChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteLoadState, setDeleteLoadState] = useState(LoadState.Idle);

  const sortColumn = props.sortColumn || 'id';
  const sortOrder = props.sortOrder || 'asc';
  const sortType = props.sortType;

  useEffect(() => {
    setAllChecked(
      selected.length > 0 && selected.length === props.data?.length
    );
    if (
      props.data &&
      selected.length > 0 &&
      selected.length < props.data.length
    ) {
      setIndeterminate(true);
    } else {
      setIndeterminate(false);
    }
  }, [selected, props.data]);

  useEffect(() => {
    setErrorMessage('');
    if (props.data === undefined) {
      setVisibleData([]);
    } else {
      if (!Array.isArray(props.data)) {
        setVisibleData([]);
        return setErrorMessage(`Fel när data hämtades: ${props.data}.`);
      }
      setVisibleData(
        props.data
          .sort((a, b) => {
            if (sortType === 'Date') {
              return sortOrder === 'asc'
                ? new Date(a[sortColumn]).getTime() -
                    new Date(b[sortColumn]).getTime()
                : new Date(b[sortColumn]).getTime() -
                    new Date(a[sortColumn]).getTime();
            }
            return sortOrder === 'asc'
              ? a[sortColumn] - b[sortColumn]
              : b[sortColumn] - a[sortColumn];
          })
          .slice(
            page * props.rowsPerPage,
            page * props.rowsPerPage + props.rowsPerPage
          )
      );
    }
  }, [page, props.rowsPerPage, props.data]);

  useEffect(() => {
    if (
      (props.data !== undefined && props.data.length < props.rowsPerPage) ||
      props.data.length === 0
    ) {
      setPage(0);
    }
  }, [page, props.data]);

  const handleDelete = async () => {
    setDeleteLoadState(LoadState.Loading);

    if (props.deleteMultiIds) {
      await props.deleteMultiIds(selected.map((s) => s.id));
    } else if (props.deleteMulti) {
      await props.deleteMulti(selected);
    } else if (props.deleteItem) {
      selected.forEach((s) => {
        props.deleteItem(s);
      });
    }
    setDeleteLoadState(LoadState.Success);
    setSelected([]);
  };

  const handleClick = (_event: React.MouseEvent<unknown>, id: number) => {
    const clickedItem = visibleData.filter((i) => i.id === id);
    const selectedIndex = selected.map((i) => i.id).indexOf(id);

    //read only
    let newSelected: T[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, clickedItem);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleSelectAll = (
    _e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => setSelected(checked && props.data ? props.data : []);

  return errorMessage ? (
    <Alert sx={{ mt: 2 }} severity={'error'}>
      {errorMessage}
    </Alert>
  ) : (
    <TableContainer>
      <MuiTable size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={indeterminate}
                checked={allChecked}
                onChange={handleSelectAll}
                size="small"
                sx={{ cursor: 'default', width: 28 }}
              />
            </TableCell>
            {props.columnHeaders.map((c) => (
              <TableCell key={c.id} sx={{ fontWeight: 700 }}>
                {c.header}
              </TableCell>
            ))}
            <TableCell style={{ textAlign: 'right' }}>
              <IconButton
                color="primary"
                disabled={
                  selected.length === 0 || deleteLoadState === LoadState.Loading
                }
                onClick={handleDelete}
              >
                {deleteLoadState === LoadState.Loading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody
          visibleData={visibleData}
          rowsPerPage={props.rowsPerPage}
          columnDefinitions={props.columnDefinitions}
          selected={selected}
          handleClick={(event, id) => handleClick(event, id)}
          page={page}
        />
        <TableFooter>
          <TableRow sx={{ borderBottom: 'none' }}>
            <TablePagination
              page={page}
              sx={{ borderBottom: 'none' }}
              rowsPerPage={props.rowsPerPage}
              count={props.data ? props.data.length : 0}
              onPageChange={(_e, newPage) => setPage(newPage)}
              rowsPerPageOptions={[props.rowsPerPage]}
              labelDisplayedRows={(p: LabelDisplayedRowsArgs) => (
                <span>
                  Visar {p.from} - {p.to} av {p.count}
                </span>
              )}
            />
          </TableRow>
        </TableFooter>
      </MuiTable>
    </TableContainer>
  );
};

export default Table;
